/**
 * useScrapersData Hook
 *
 * Manages scrapers and their associated scrapes from Firestore
 * Similar to useCampaignsData but for scrapers
 */

import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../components/shared";

export function useScrapersData() {
  const toast = useToast();
  const [scrapers, setScrapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  // Fetch scrapers and their associated scrapes.
  //
  // Per-scraper scrape listeners are managed incrementally: we only add a listener
  // when a new scraper appears and remove one when a scraper is deleted. Previously
  // EVERY parent-snapshot tore down and rebuilt ALL child listeners (even when only a
  // single scraper field changed) and called setScrapers once per child — causing
  // listener churn and a burst of re-renders. State is now published in a single
  // batched call via publish().
  useEffect(() => {
    const scrapersQuery = query(
      collection(db, "discovery_scrapers"),
      orderBy("name", "asc")
    );

    const scrapeListeners = new Map(); // scraperId -> unsubscribe
    const scraperData = new Map(); // scraperId -> latest scraper doc (query order)
    const scrapesData = new Map(); // scraperId -> latest scrapes array

    // Combine the latest scraper docs + their scrapes into one array and push to state.
    const publish = () => {
      const combined = [];
      scraperData.forEach((scraper, id) => {
        const scrapes = scrapesData.get(id) || [];
        const totalScrapes = scrapes.length;
        const completedScrapes = scrapes.filter(
          (s) => s.status === "completed"
        ).length;
        const runningScrapes = scrapes.filter(
          (s) => s.status === "running" || s.status === "claimed"
        ).length;
        const failedScrapes = scrapes.filter(
          (s) => s.status === "failed"
        ).length;
        const totalPropertiesScraped = scrapes.reduce(
          (sum, scrape) =>
            sum + (scrape.totalProperties || scrape.successfulProperties || 0),
          0
        );
        combined.push({
          ...scraper,
          scrapes,
          totalScrapes,
          completedScrapes,
          runningScrapes,
          failedScrapes,
          totalPropertiesScraped,
          completionRate:
            totalScrapes > 0
              ? Math.round((completedScrapes / totalScrapes) * 100)
              : 0,
        });
      });
      setScrapers(combined);
    };

    const unsubscribeScrapers = onSnapshot(
      scrapersQuery,
      (snapshot) => {
        try {
          // Refresh scraper docs, preserving query (name) order.
          const nextIds = new Set();
          scraperData.clear();
          snapshot.docs.forEach((scraperDoc) => {
            const scraper = { id: scraperDoc.id, ...scraperDoc.data() };
            scraperData.set(scraper.id, scraper);
            nextIds.add(scraper.id);
          });

          // Tear down listeners for scrapers that no longer exist.
          scrapeListeners.forEach((unsubscribe, id) => {
            if (!nextIds.has(id)) {
              unsubscribe();
              scrapeListeners.delete(id);
              scrapesData.delete(id);
            }
          });

          // Add a scrape listener only for scrapers we aren't already watching.
          scraperData.forEach((scraper) => {
            if (!scraper.scraperId) {
              console.warn(
                `Scraper ${scraper.id} missing scraperId field, skipping scrape listener`
              );
              return;
            }
            if (scrapeListeners.has(scraper.id)) return;

            try {
              const scrapesQuery = query(
                collection(db, "discovery_scrapes"),
                where("createdBy", "==", scraper.scraperId),
                orderBy("createdAt", "desc")
              );
              const scraperId = scraper.id;
              const unsubscribeScrapes = onSnapshot(
                scrapesQuery,
                (scrapesSnapshot) => {
                  scrapesData.set(
                    scraperId,
                    scrapesSnapshot.docs.map((doc) => ({
                      id: doc.id,
                      ...doc.data(),
                    }))
                  );
                  publish();
                },
                (error) => {
                  console.warn(
                    `Failed to fetch scrapes for ${scraper.scraperId}:`,
                    error.message
                  );
                }
              );
              scrapeListeners.set(scraperId, unsubscribeScrapes);
            } catch (error) {
              console.warn(
                `Failed to set up scrape listener for ${scraper.scraperId}:`,
                error.message
              );
            }
          });

          // One batched publish with the freshest scraper data we have.
          publish();
          setLoading(false);
        } catch (error) {
          console.error("Error fetching scrapers:", error);
          toast.error("Failed to load scrapers");
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error in scrapers snapshot:", error);
        toast.error("Failed to load scrapers");
        setLoading(false);
      }
    );

    return () => {
      unsubscribeScrapers();
      scrapeListeners.forEach((unsubscribe) => unsubscribe());
    };
  }, [toast]);

  // Create scraper
  const createScraper = useCallback(
    async (scraperData) => {
      try {
        const newScraper = {
          name: scraperData.name.trim(),
          url: scraperData.url.trim(),
          scraperId: scraperData.scraperId.trim(),
          platform: scraperData.platform,
          availability: scraperData.availability ?? true,
          healthStatus: scraperData.healthStatus || "unknown",
          lastHealthCheck: null,
          lastUsed: null,
          notes: scraperData.notes?.trim() || "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        const docRef = await addDoc(
          collection(db, "discovery_scrapers"),
          newScraper
        );
        toast.success("Scraper created successfully");
        return docRef.id;
      } catch (error) {
        console.error("Error creating scraper:", error);
        if (error.code === "permission-denied") {
          toast.error("Permission denied: Only admins can create scrapers.");
        } else {
          toast.error("Failed to create scraper: " + error.message);
        }
        return null;
      }
    },
    [toast]
  );

  // Delete scraper
  const deleteScraper = useCallback(
    async (scraperId) => {
      if (
        !window.confirm(
          "Are you sure you want to delete this scraper? This cannot be undone."
        )
      ) {
        return false;
      }

      try {
        setDeleting(scraperId);
        await deleteDoc(doc(db, "discovery_scrapers", scraperId));
        toast.success("Scraper deleted successfully");
        return true;
      } catch (error) {
        console.error("Error deleting scraper:", error);
        toast.error("Failed to delete scraper");
        return false;
      } finally {
        setDeleting(null);
      }
    },
    [toast]
  );

  return {
    scrapers,
    loading,
    deleting,
    createScraper,
    deleteScraper,
  };
}

export default useScrapersData;
